import { createColumnHelper } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type { DivergingField } from "~/modules/billing/utils/billingSettingsDisplayUtils.ts";

import type { DivergingOrderBillingSettingsRow } from "./divergingOrderBillingSettingsTableUtils.ts";

const columnHelper = createColumnHelper<DivergingOrderBillingSettingsRow>();

const NOT_DIVERGING_VALUE = <span className="text-sm font-light text-gray-400/90">wie Projekt</span>;

function renderDivergingText(field: DivergingField<string | null>): ReactNode {
	if (!field.isDifferent) return NOT_DIVERGING_VALUE;
	return field.displayValue ? field.displayValue : EMPTY_VALUE_PLACEHOLDER;
}

export const divergingOrderBillingSettingsTableColumnDefs = [
	columnHelper.accessor("phaseAndOrderName", {
		header: "Phase - Bestellung",
		meta: {
			headerTextAlign: "left",
			cellTextAlign: "left",
		},
		cell: (props) => {
			const value = props.getValue();
			return <NavLink className="hover:text-danger-500"
							to={value.url}>{value.displayName}</NavLink>;
		},
	}),
	columnHelper.accessor("invoiceTypeId", {
		header: "Rechnungstyp",
		cell: (props) => renderDivergingText(props.getValue()),
	}),
	columnHelper.accessor("isFixedPrice", {
		header: "Abrechnungsart",
		cell: (props) => renderDivergingText(props.getValue()),
	}),
	columnHelper.accessor("isSkillBased", {
		header: "Abrechnung Rollen",
		cell: (props) => renderDivergingText(props.getValue()),
	}),
	columnHelper.accessor("travelExpensesIncluded", {
		header: "Reisekosten",
		cell: (props) => renderDivergingText(props.getValue()),
	}),
	columnHelper.accessor("isBilledMonthly", {
		header: "Abrechnungsintervall",
		cell: (props) => renderDivergingText(props.getValue()),
	}),
	columnHelper.accessor("paymentTargetDays", {
		header: "Zahlungsziel",
		cell: (props) => renderDivergingText(props.getValue()),
	}),
	columnHelper.accessor("currencyDisplayName", {
		header: "Währung",
		cell: (props) => renderDivergingText(props.getValue()),
	}),
	columnHelper.accessor("resolvedInvoiceRecipient", {
		header: "Rechnungsempfänger",
		cell: (props) => {
			const field = props.getValue();
			if (!field.isDifferent) return NOT_DIVERGING_VALUE;
			if (!field.displayValue) return EMPTY_VALUE_PLACEHOLDER;
			return (
				<NavLink className="hover:text-danger-500"
						 to={field.displayValue.url}>
					{field.displayValue.displayName}
				</NavLink>
			);
		},
		sortingFn: (rowA, rowB) => {
			const a = rowA.original.resolvedInvoiceRecipient.displayValue?.displayName ?? "";
			const b = rowB.original.resolvedInvoiceRecipient.displayValue?.displayName ?? "";
			return a.localeCompare(b);
		},
	}),
	columnHelper.accessor("invoiceByMail", {
		header: "Versand per",
		cell: (props) => renderDivergingText(props.getValue()),
	}),
	columnHelper.accessor("additionalEmailRecipients", {
		header: "Weitere E-Mail-Empfänger",
		cell: (props) => renderDivergingText(props.getValue()),
		enableSorting: false,
	}),
	columnHelper.accessor("billingInstructions", {
		header: "Hinweise zur Abrechnung",
		cell: (props) => renderDivergingText(props.getValue()),
	}),
];